import _ from "lodash";
import { amber } from "@mui/material/colors";

export default class Coverage {
  /**
   * Extract the numeric value from a CI/RCI level string
   * @param string - Level string in format "CI-X", "RCI-X", or "CI-(-1)"
   * @returns The numeric value, or null if invalid
   */
  static extractNumber(string: string): number | null {
    if (string === "CI-(-1)") {
      return -1;
    }
    // Use a regex that matches the number after CI- or RCI-
    const match = string.match(/(?:CI|RCI)-(-?\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Generate all available coverage level options
   * @returns Array of coverage level strings in order: CI-(-1), CI-0 to CI-10, CI-11, RCI-0 to RCI-10
   */
  static options(): string[] {
    const options: string[] = [];

    // Add "I'm not restoring" option (CI-(-1))
    options.push("CI-(-1)");

    // Add CI-0 to CI-10
    for (let i = 0; i <= 10; i++) {
      options.push(`CI-${i}`);
    }

    // Add RCI-0 to RCI-10
    for (let i = 0; i <= 10; i++) {
      options.push(`RCI-${i}`);
    }

    return options;
  }

  /**
   * Get the display label for a coverage level option
   * @param option - Coverage level string (e.g., "CI-5", "CI-(-1)", "CI-11")
   * @returns Human-readable display label
   */
  static label(option: string): string {
    if (option === "CI-(-1)") {
      return "I'm not restoring";
    }
    return option;
  }

  /**
   * Get the color strength (amber shade) for a coverage level number
   * @param number - The numeric coverage level (0-10, or -1 for not restoring, 11 for intact)
   * @returns Material-UI amber color string (e.g., "#ffb300")
   */
  static color(number: number): string {
    const clamped = _.clamp(number, 1, 9);
    const shade = (clamped * 100) as keyof typeof amber;
    return amber[shade];
  }
}

// Legacy exports for backward compatibility
export const extractNumberFromString = Coverage.extractNumber;
export const generateCoverageOptions = Coverage.options;
export const getCoverageDisplayLabel = Coverage.label;
export const getCoverageColorStrength = Coverage.color;
